import React from "react";

import { Container, Grid } from "@mui/material";

import CustomInput from "../../../../helper-components/input/CustomInput";
import CustomStepper from "../../../../helper-components/form/CustomStepper";
import ConfirmationDialog from "../../../../helper-components/input/ConfirmationDialog";
import TransparentFragment from "../../../../helper-components/misc/TransparentFragment";

import CustomSelection from "../../../../helper-components/form/CustomSelection";

import { useDialog } from "../../../../helper-functions/hooks/useDialog";
import { querySelectTextTranslations } from "../../../../helper-functions/api/translationQueries";
import { addFormToObj, getEllipsis, setInsertObjectState, setValue } from "../../../../helper-functions/shared/miscUtils";
import { formHasValue, validateRow } from "../../../../helper-functions/validation/objectValidation";

import { AUDIO_TRANSLATION_VALIDATION } from "../../../../helper-functions/validation/translationValidation";
import { AUDIO_TRANSLATION_OBJECT, DEFAULT_TRANSLATE_INSTRUCTIONS, VOICE_OPTIONS } from "../../../../config/constants/Constants";

/**
 * A stepper component to add a new resource.
 * Includes helper descriptions and validation.
 */
export default function AudioTranslationAddStepper(props) {
    const { enqueueDialog, setOpen, ...options } = useDialog(); // Nested dialog component.

    // Because modals don't re-render if the props' state changes, use a callback.
    const { callback, row: existingRow = {} } = props;

    const object = AUDIO_TRANSLATION_OBJECT;
    const validation = AUDIO_TRANSLATION_VALIDATION;

    const [row, setRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(object))));

    // Initialize the temporary row object with existing values.
    React.useEffect(() => {
        const newRow = {
            ...row,
            ...existingRow,

            _voice: existingRow.meta_data?.voice ?? "alloy",
            _instructions: existingRow.meta_data?.instructions ?? getInstructions(),

            form: {
                ...row.form,
                ...existingRow.form,
                saved: false // Required to allow editing input fields.
            }
        };

        setInsertObjectState(setRow, newRow);
    }, []);

    const getInstructions = (language) => {
        const accent = language ? "\nAccent: Native " + language + ".\n" : "";
        const instructions = DEFAULT_TRANSLATE_INSTRUCTIONS.replace("<accent>", accent);

        return instructions;
    };

    /**
     * Executed when typing something into an input field.
     */
    const onChange = (obj, key, value, lang = null) => {
        const newRow = JSON.parse(JSON.stringify(row));
        setValue(newRow, key, value);

        if (key === "translation_id") {
            newRow.meta_data.instructions = getInstructions(lang);
            newRow._instructions = getInstructions(lang);
        }

        if (key === "_voice") {
            newRow.meta_data.voice = value;
        }

        if (key === "_instructions") {
            newRow.meta_data.instructions = value;
        }

        validateRow(obj, validation, newRow);
        setInsertObjectState(setRow, newRow);
    };

    const handleClick = (confirm) => {
        const options = { row };

        callback(confirm, options);
    };

    const handleTranslation = (selected = []) => {
        const ids = selected.map((el) => el.id);
        onChange(row, "translation_id", ids?.[0], selected[0]?.language);
    };

    // Create the step components.
    const step1 = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment value="Select the translation to convert to speech." />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomSelection
                            {...props}
                            callback={handleTranslation}
                            query={querySelectTextTranslations}
                            getPrimarySearchText={(el) => el?.resource?.name}
                            getSecondarySearchText={(el) => el?.text}
                            getPrimaryText={(el) => getEllipsis(el?.resource?.name, 50)}
                            getSecondaryText={(el) => getEllipsis(el?.text, 50)}
                        />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const step2 = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment value={`Define the how the speech should sound.`} />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput name="_voice" row={row} onChange={onChange} label="Voice" select={VOICE_OPTIONS} />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput name="_instructions" row={row} onChange={onChange} label="Instructions" multiline minRows={2} maxRows={10} />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const step3 = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment
                            value={`Creating new audio translation with the following details:
                            Translation ID: ${row?.translation_id || "N/A"}
                            Voice: ${row._voice || "N/A"}

                            Click "Apply" to save the request or "Reset" to start over.`}
                        />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    // Define the steps and the final step.
    const steps = [
        {
            label: "Select translation",
            optional: false,
            component: step1(),
            nextDisabled: !formHasValue(row, "translation_id")
        },
        {
            label: "Additional details",
            optional: false,
            component: step2(),
            nextDisabled: !formHasValue(row, "_voice")
        }
    ];

    const final = { component: step3() };

    return (
        <>
            <ConfirmationDialog
                {...props}
                options={{
                    enqueueDialog,
                    setOpen,
                    ...options
                }}
            />

            <CustomStepper steps={steps} final={final} callback={(confirm) => handleClick(confirm)} />
        </>
    );
}
